import { z } from "zod";

/**
 * Sprint 4 #1 — Reagent inventory (ISO 15189 §5.3).
 *
 * Three coupled API resources mirroring the DB tables:
 *   - Catalog (master list per clinic)
 *   - Lots (physical batches with expiry + remaining qty)
 *   - Consumptions (append-only audit log)
 *
 * Numeric quantities are exchanged as JSON strings (same as `price` in
 * the lab catalogue) to avoid IEEE-754 drift across the wire. The server
 * casts to numeric on insert + back to string on read.
 */

// ===== Category enum =====

export const reagentCategorySchema = z.enum([
	"chemistry",
	"hematology",
	"immunology",
	"microbiology",
	"consumable",
	"other",
]);
export type ReagentCategory = z.infer<typeof reagentCategorySchema>;

// ===== Catalog =====

const quantityStringSchema = z
	.string()
	.regex(/^\d{1,12}(\.\d{1,4})?$/u, "Invalid quantity");

export const reagentCatalogSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	name: z.string(),
	category: reagentCategorySchema.nullable(),
	unit: z.string().nullable(),
	reorder_threshold: z.string().nullable(),
	/** Order-up-to level for the reorder suggestion (defaults to 2× threshold). */
	reorder_target_quantity: z.string().nullable(),
	/** Preferred supplier — groups the purchase-order suggestion. */
	supplier_name: z.string().nullable(),
	/** Supplier phone/email so the PO suggestion is actionable. */
	supplier_contact: z.string().nullable(),
	notes: z.string().nullable(),
	is_active: z.boolean(),
	created_at: z.string().datetime(),
	updated_at: z.string().datetime(),
	/** Computed on list endpoint: SUM(active lots.current_quantity). */
	total_on_hand: z.string().optional(),
	/** Computed: true when total_on_hand < reorder_threshold (or threshold null = false). */
	is_low_stock: z.boolean().optional(),
});
export type ReagentCatalogApi = z.infer<typeof reagentCatalogSchema>;

export const createReagentCatalogInputSchema = z.object({
	name: z.string().min(1).max(200),
	category: reagentCategorySchema.nullable().optional(),
	unit: z.string().max(40).nullable().optional(),
	reorder_threshold: quantityStringSchema.nullable().optional(),
	reorder_target_quantity: quantityStringSchema.nullable().optional(),
	supplier_name: z.string().max(200).nullable().optional(),
	supplier_contact: z.string().max(200).nullable().optional(),
	notes: z.string().max(2000).nullable().optional(),
});
export type CreateReagentCatalogInput = z.infer<
	typeof createReagentCatalogInputSchema
>;

export const updateReagentCatalogInputSchema = createReagentCatalogInputSchema
	.partial()
	.extend({ is_active: z.boolean().optional() });
export type UpdateReagentCatalogInput = z.infer<
	typeof updateReagentCatalogInputSchema
>;

// ===== Lots =====

export const reagentLotSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	catalog_id: z.string().uuid(),
	lot_number: z.string(),
	manufacturer: z.string().nullable(),
	expiry_date: z.string().nullable(),
	received_at: z.string(),
	initial_quantity: z.string(),
	current_quantity: z.string(),
	unit_cost: z.string().nullable(),
	notes: z.string().nullable(),
	is_active: z.boolean(),
	created_at: z.string().datetime(),
	updated_at: z.string().datetime(),
	/** Catalog name surfaced for the lots list UI. */
	catalog_name: z.string().optional(),
	/** True when expiry_date <= today + 30 days (and active). */
	expires_soon: z.boolean().optional(),
});
export type ReagentLotApi = z.infer<typeof reagentLotSchema>;

export const createReagentLotInputSchema = z.object({
	catalog_id: z.string().uuid(),
	lot_number: z.string().min(1).max(120),
	manufacturer: z.string().max(200).nullable().optional(),
	expiry_date: z
		.string()
		.regex(/^\d{4}-\d{2}-\d{2}$/u, "Use YYYY-MM-DD")
		.nullable()
		.optional(),
	received_at: z
		.string()
		.regex(/^\d{4}-\d{2}-\d{2}$/u, "Use YYYY-MM-DD")
		.optional(),
	initial_quantity: quantityStringSchema,
	unit_cost: quantityStringSchema.nullable().optional(),
	notes: z.string().max(2000).nullable().optional(),
});
export type CreateReagentLotInput = z.infer<typeof createReagentLotInputSchema>;

export const updateReagentLotInputSchema = z.object({
	lot_number: z.string().min(1).max(120).optional(),
	manufacturer: z.string().max(200).nullable().optional(),
	expiry_date: z
		.string()
		.regex(/^\d{4}-\d{2}-\d{2}$/u)
		.nullable()
		.optional(),
	unit_cost: quantityStringSchema.nullable().optional(),
	notes: z.string().max(2000).nullable().optional(),
	is_active: z.boolean().optional(),
});
export type UpdateReagentLotInput = z.infer<typeof updateReagentLotInputSchema>;

// ===== Consumption =====

export const reagentReasonSchema = z.enum([
	"test",
	"qc",
	"calibration",
	"waste",
	"manual_adjustment",
]);
export type ReagentReason = z.infer<typeof reagentReasonSchema>;

export const reagentConsumptionSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	lot_id: z.string().uuid(),
	catalog_id: z.string().uuid(),
	quantity_used: z.string(),
	reason: reagentReasonSchema,
	reference_order_id: z.string().uuid().nullable(),
	recorded_by_member_id: z.string().uuid().nullable(),
	notes: z.string().nullable(),
	consumed_at: z.string().datetime(),
	created_at: z.string().datetime(),
	catalog_name: z.string().optional(),
	lot_number: z.string().optional(),
});
export type ReagentConsumptionApi = z.infer<typeof reagentConsumptionSchema>;

export const recordReagentConsumptionInputSchema = z.object({
	lot_id: z.string().uuid(),
	quantity_used: quantityStringSchema,
	reason: reagentReasonSchema,
	reference_order_id: z.string().uuid().nullable().optional(),
	notes: z.string().max(2000).nullable().optional(),
});
export type RecordReagentConsumptionInput = z.infer<
	typeof recordReagentConsumptionInputSchema
>;

// ===== Reports =====

export const reagentLowStockRowSchema = z.object({
	catalog: reagentCatalogSchema,
	total_on_hand: z.string(),
	reorder_threshold: z.string().nullable(),
});
export type ReagentLowStockRow = z.infer<typeof reagentLowStockRowSchema>;

export const reagentExpiringRowSchema = z.object({
	lot: reagentLotSchema,
	days_until_expiry: z.number().int(),
});
export type ReagentExpiringRow = z.infer<typeof reagentExpiringRowSchema>;

// ===== Reorder suggestion (auto-reorder) =====

/** One reagent that is below its reorder point, with how much to order. */
export const reorderSuggestionItemSchema = z.object({
	catalog_id: z.string().uuid(),
	name: z.string(),
	unit: z.string().nullable(),
	category: reagentCategorySchema.nullable(),
	on_hand: z.string(),
	reorder_threshold: z.string(),
	/** Order-up-to level used (explicit target or the 2× default). */
	target_quantity: z.string(),
	/** ceil(target - on_hand), at least 1 — the proposed order quantity. */
	suggested_order_quantity: z.string(),
});
export type ReorderSuggestionItem = z.infer<typeof reorderSuggestionItemSchema>;

/** Suggested order lines grouped by their preferred supplier. */
export const reorderSuggestionGroupSchema = z.object({
	supplier_name: z.string().nullable(),
	supplier_contact: z.string().nullable(),
	items: z.array(reorderSuggestionItemSchema),
});
export type ReorderSuggestionGroup = z.infer<
	typeof reorderSuggestionGroupSchema
>;

export const reorderSuggestionsResponseSchema = z.object({
	generated_at: z.string().datetime(),
	total_items: z.number().int(),
	groups: z.array(reorderSuggestionGroupSchema),
});
export type ReorderSuggestionsResponse = z.infer<
	typeof reorderSuggestionsResponseSchema
>;
